/**
 * useOnboardingTooltip  —  script layer
 *
 * Owns all positioning logic and wires the animation hook.
 * Returns a flat object that the UI component destructures directly,
 * keeping the JSX free of any positioning or animation concerns.
 *
 * Positioning rules:
 *   1. Prefer the step's explicit `placement` (top/bottom/left/right).
 *   2. Fall back to 'auto': pick the side with the most available space.
 *   3. Clamp to keep the card fully inside the viewport.
 *   4. Resolve any remaining overlap with the spotlight rect.
 *   5. Modal steps (or missing rect) → center in the viewport.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

import type { SpotlightRect } from '../components/SpotlightOverlay';
import type { GuideStep, TooltipPlacement } from '../config/types';

import { useTooltipAnimation } from './useTooltipAnimation';

// ─── Layout constants ────────────────────────────────────────────────────────

const CARD_W = 420;
const GAP = 20; // gap between card edge and spotlight highlight
const EDGE = 12; // minimum card-to-viewport-edge margin

// ─── Pure position utilities ─────────────────────────────────────────────────

type CardPos = { top: number; left: number };

/**
 * After viewport-clamping, push the card away from the spotlight rect if they
 * still overlap. Prefers the side (above/below) that has more room.
 */
const resolveOverlap = (
  pos: CardPos,
  cardH: number,
  rect: SpotlightRect,
): CardPos => {
  const vh = window.innerHeight;
  const { x: rx, y: ry, width: rw, height: rh } = rect;

  const overlapsV = pos.top + cardH > ry && pos.top < ry + rh;
  const overlapsH = pos.left + CARD_W > rx && pos.left < rx + rw;
  if (!overlapsV || !overlapsH) return pos;

  const spaceBelow = vh - (ry + rh) - EDGE;
  const spaceAbove = ry - EDGE;

  if (spaceBelow >= cardH) return { ...pos, top: ry + rh + GAP };
  if (spaceAbove >= cardH) return { ...pos, top: ry - cardH - GAP };
  return { ...pos, top: Math.min(ry + rh + GAP, vh - cardH - EDGE) };
};

/** Compute the card position relative to a spotlight rect and placement hint. */
const computePosition = (
  rect: SpotlightRect,
  placement: TooltipPlacement,
  cardH: number,
): CardPos => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { x, y, width, height } = rect;

  const sides: Record<Exclude<TooltipPlacement, 'auto'>, CardPos> = {
    top: { top: y - cardH - GAP, left: x + width / 2 - CARD_W / 2 },
    bottom: { top: y + height + GAP, left: x + width / 2 - CARD_W / 2 },
    left: { top: y + height / 2 - cardH / 2, left: x - CARD_W - GAP },
    right: { top: y + height / 2 - cardH / 2, left: x + width + GAP },
  };

  const chosen: Exclude<TooltipPlacement, 'auto'> =
    placement === 'auto'
      ? (Object.entries({
          top: y,
          bottom: vh - (y + height),
          left: x,
          right: vw - (x + width),
        }).sort((a, b) => b[1] - a[1])[0][0] as Exclude<
          TooltipPlacement,
          'auto'
        >)
      : placement;

  const clamped: CardPos = {
    top: Math.max(EDGE, Math.min(sides[chosen].top, vh - cardH - EDGE)),
    left: Math.max(EDGE, Math.min(sides[chosen].left, vw - CARD_W - EDGE)),
  };

  return resolveOverlap(clamped, cardH, rect);
};

/** Center the card in the viewport (used for modal steps and missing rects). */
const centerPosition = (cardH: number): CardPos => ({
  top: Math.max(EDGE, window.innerHeight / 2 - cardH / 2),
  left: Math.max(EDGE, window.innerWidth / 2 - CARD_W / 2),
});

// ─── Hook ────────────────────────────────────────────────────────────────────

type UseOnboardingTooltipInput = {
  step: GuideStep;
  rect: SpotlightRect | null;
  currentStep: number;
  totalSteps: number;
  onPause: () => void;
};

export type UseOnboardingTooltipResult = {
  cardRef: RefObject<HTMLDivElement>;
  cardStyle: CSSProperties;
  isFirst: boolean;
  isLast: boolean;
  /** Call from the X button to begin the dismiss animation. */
  startDismiss: () => void;
  /** Attach to onTransitionEnd on the card div. */
  handleTransitionEnd: () => void;
};

export const useOnboardingTooltip = ({
  step,
  rect,
  currentStep,
  totalSteps,
  onPause,
}: UseOnboardingTooltipInput): UseOnboardingTooltipResult => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<CardPos>({ top: 0, left: 0 });

  const { isOffscreen, transitionStyle, startDismiss, handleTransitionEnd } =
    useTooltipAnimation(step.id, onPause);

  // Recompute position whenever the step or spotlight rect changes.
  // useLayoutEffect fires before paint, so pos is always correct before isOffscreen flips false.
  useLayoutEffect(() => {
    const cardH = cardRef.current?.offsetHeight ?? 200;
    setPos(
      step.type === 'modal' || !rect
        ? centerPosition(cardH)
        : computePosition(rect, step.placement ?? 'auto', cardH),
    );
  }, [step, rect]);

  // Off-screen origin: above the viewport, horizontally centered.
  const cardH = cardRef.current?.offsetHeight ?? 200;
  const originTop = -(cardH + EDGE);
  const originLeft = Math.max(EDGE, window.innerWidth / 2 - CARD_W / 2);

  const cardStyle: CSSProperties = {
    position: 'fixed',
    top: isOffscreen ? originTop : pos.top,
    left: isOffscreen ? originLeft : pos.left,
    width: CARD_W,
    zIndex: 10000,
    opacity: isOffscreen ? 0 : 1,
    transition: transitionStyle,
  };

  return {
    cardRef,
    cardStyle,
    isFirst: currentStep === 0,
    isLast: currentStep === totalSteps - 1,
    startDismiss,
    handleTransitionEnd,
  };
};
